// src/api/legal.js
import api from "./axiosInstance";

/**
 * Fetches the approved content (metadata + renderable body) for a legal
 * document from the regional backend, resolved for the clinician dashboard
 * surface and the requested locale. The Consent Service owns the content; the
 * dashboard renders what it returns rather than a bundled local copy.
 *
 * @param {string} documentType - "terms" or "privacy".
 * @param {{ locale?: string, surface?: string }} [params={}] - Resolution inputs.
 * @returns {Promise<object|null>} Resolved document, or null on failure.
 */
export async function fetchLegalDocument(documentType, params = {}) {
  const { locale, surface = "dashboard" } = params;
  const query = { documentType, surface };

  if (locale) {
    query.locale = locale;
  }

  const startedAt = Date.now();

  try {
    const { data } = await api.get("/v1/legal/documents", { params: query });
    return data?.document || null;
  } catch (error) {
    if (process.env.NODE_ENV !== "test") {
      console.error("[legal-api] fetchLegalDocument failed", {
        documentType,
        locale: locale || null,
        surface,
        elapsedMs: Date.now() - startedAt,
        status: error?.response?.status || null,
        response: error?.response?.data || null,
        message: error?.message || null,
      });
    }
    throw error;
  }
}

/**
 * Records a legal action (e.g. acknowledgement) for the authenticated user
 * against the regional Consent Service.
 *
 * @param {{
 *   documentType: string,
 *   version: string,
 *   locale: string,
 *   action: string,
 *   actionMethod?: string,
 *   clinicStudyId?: string | null,
 * }} payload
 * @returns {Promise<object>} The consent-service response.
 */
export async function recordLegalAction(payload) {
  const startedAt = Date.now();

  try {
    const { data } = await api.post("/v1/legal/records", payload);
    return data;
  } catch (error) {
    if (process.env.NODE_ENV !== "test") {
      console.error("[legal-api] recordLegalAction failed", {
        elapsedMs: Date.now() - startedAt,
        status: error?.response?.status || null,
        code: error?.code || null,
        response: error?.response?.data || null,
        message: error?.message || null,
      });
    }
    throw error;
  }
}
